import type { z } from "zod";

import type { featureFlagSchema } from "@blis/shared/schemas/settings";
import { trpc } from "@blis/trpc/react";

import { Loader } from "@/components/ui/loader";
import { Switch, SwitchGroup } from "@/components/ui/switch";
import { Text } from "@/components/ui/text";
import { mutationResponse } from "@/utils/trpc";
import { SettingsGroup } from "./SettingsGroup";

type FormValues = z.infer<typeof featureFlagSchema>;

type FeatureFlagItem = {
  key: keyof FormValues;
  name: string;
};

// Add keys here for them to be displayed in the UI
const featureFlags: FeatureFlagItem[] = [
  {
    key: "enableLabelsV2",
    name: "Label engine V2",
  },
  {
    key: "enableSerologyReportResultSampleId",
    name: "Serology report: sample used on result rows",
  },
  {
    key: "holdBlocksFinalReport",
    name: "Active hold blocks final report release",
  },
  {
    key: "enableReflexReconcile",
    name: "Enable reflex reconciliation",
  },
];

export function FeatureFlagSettings() {
  const { data, isLoading } = trpc.settings.featureFlags.useQuery();

  return (
    <SettingsGroup title="Features">
      <SwitchGroup>
        {isLoading ? (
          <Loader />
        ) : featureFlags.length > 0 ? (
          featureFlags.map((flag) => (
            <FeatureFlagSwitch
              key={flag.key}
              name={flag.key}
              description={flag.name}
              value={data?.[flag.key] || false}
            />
          ))
        ) : (
          <Text muted>No feature flags available</Text>
        )}
      </SwitchGroup>
    </SettingsGroup>
  );
}

function FeatureFlagSwitch({
  name,
  description,
  value,
}: {
  name: keyof FormValues;
  description: string;
  value: boolean;
}) {
  const update = trpc.settings.updateFeatureFlag.useMutation();
  const onCheckedChange = (val: boolean) => {
    update.mutate(
      { [name]: val.toString() },
      mutationResponse({
        errorTitle: "Error updating setting",
        successMessage: `${description} ${val ? "enabled" : "disabled"}`,
      }),
    );
  };
  return (
    <Switch defaultSelected={value} onChange={onCheckedChange}>
      Enable {description.toLowerCase()}
    </Switch>
  );
}
